import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    BadgeCheck,
    CheckCircle2,
    Landmark,
    Loader2,
    Mail,
    Save,
    ShieldCheck,
    Smartphone,
    User,
} from "lucide-react";
import { toast } from "sonner";
import config from "../config";

const EMPTY_PROFILE = { name: "", mobile: "", email: "", cspid: "", acno: "" };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function readJson(response) {
    const text = await response.text();
    if (!text.trim()) throw new Error(`Server returned an empty response. HTTP ${response.status}.`);
    try { return JSON.parse(text); }
    catch {
        console.error("Invalid profile response:", text);
        throw new Error("The server returned an invalid response. Check the PHP error log.");
    }
}

function validate(profile) {
    const errors = {};
    const name = profile.name.trim();
    if (!name) errors.name = "Please enter your name.";
    else if (name.length > 25) errors.name = "Name must not be greater than 25 characters.";
    else if (!/^[A-Za-z][A-Za-z .'-]*$/.test(name)) errors.name = "Please enter a valid name.";
    if (!EMAIL_PATTERN.test(profile.email.trim())) errors.email = "Please enter a valid email address.";
    if (!/^[A-Za-z0-9]{8}$/.test(profile.cspid)) errors.cspid = "CSP ID must contain exactly 8 letters or numbers.";
    if (!/^\d{11,20}$/.test(profile.acno)) errors.acno = "Account number must contain 11 to 20 digits.";
    return errors;
}

export default function Profile({ endpoint = `${config.API_URL}/profile.php` }) {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(EMPTY_PROFILE);
    const [savedProfile, setSavedProfile] = useState(EMPTY_PROFILE);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState("");

    const hasChanges = useMemo(
        () => JSON.stringify(profile) !== JSON.stringify(savedProfile),
        [profile, savedProfile]
    );
    const initial = profile.name.trim().charAt(0).toUpperCase() || "U";

    useEffect(() => {
        let active = true;

        const loadProfile = async () => {
            try {
                const response = await fetch(endpoint, {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                    headers: { Accept: "application/json" },
                });
                const result = await readJson(response);

                if (!response.ok || !result.success || !result.profile) {
                    throw new Error(result.message || "Unable to load your profile.");
                }

                if (active) {
                    setProfile(result.profile);
                    setSavedProfile(result.profile);
                }
            } catch (error) {
                if (active) toast.error(error instanceof Error ? error.message : "Unable to load your profile.");
            } finally {
                if (active) setLoading(false);
            }
        };

        loadProfile();
        return () => { active = false; };
    }, [endpoint]);

    const handleChange = ({ target: { name, value } }) => {
        let nextValue = value;
        if (name === "name") nextValue = value.slice(0, 25);
        if (name === "cspid") nextValue = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 8);
        if (name === "acno") nextValue = value.replace(/\D/g, "").slice(0, 20);
        setProfile((current) => ({ ...current, [name]: nextValue }));
        setErrors((current) => ({ ...current, [name]: "" }));
        setSuccess("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (saving) return;

        const validationErrors = validate(profile);
        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            toast.error("Please correct the highlighted fields.");
            return;
        }

        setSaving(true);
        setSuccess("");

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    name: profile.name.trim().toUpperCase(),
                    email: profile.email.trim(),
                    cspid: profile.cspid.trim().toUpperCase(),
                    acno: profile.acno,
                }),
            });
            const result = await readJson(response);

            if (!response.ok || !result.success) {
                if (result.errors && typeof result.errors === "object") setErrors(result.errors);
                throw new Error(result.message || "Profile could not be updated.");
            }

            const updated = result.profile || profile;
            setProfile(updated);
            setSavedProfile(updated);
            setSuccess(result.message || "Profile updated successfully.");
            toast.success(result.message || "Profile updated successfully.");
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Unable to update your profile.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <main className="min-h-[calc(100vh-65px)] flex items-center justify-center">
                <div className="glass-card rounded-2xl px-6 py-5 flex items-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" /> Loading your profile…
                </div>
            </main>
        );
    }

    return (
        <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
            <div className="pointer-events-none absolute inset-0 flex justify-center">
                <div className="mt-8 h-[520px] w-[520px] rounded-full bg-gradient-to-tr from-[#3BC7FF]/20 to-[#7B61FF]/20 blur-3xl" />
            </div>

            <div className="relative mx-auto w-full max-w-3xl fade-in-up">
                <button type="button" onClick={() => navigate(-1)} className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Back
                </button>

                <section className="glass-card rounded-3xl border border-border/70 overflow-hidden shadow-xl">
                    <div className="relative border-b border-border/70 p-6 sm:p-8">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/[0.06] to-violet-500/[0.06]" />
                        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center">
                            <div className="h-20 w-20 shrink-0 rounded-3xl btn-gradient flex items-center justify-center text-3xl font-display font-bold text-white shadow-lg">{initial}</div>
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">My Account</p>
                                <h1 className="mt-1 text-2xl sm:text-3xl font-display font-bold truncate">{profile.name || "Your Profile"}</h1>
                                <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><Smartphone className="h-4 w-4" />{profile.mobile}</p>
                            </div>
                            <div className="sm:ml-auto inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-600"><ShieldCheck className="h-4 w-4" />Verified account</div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 sm:p-8" noValidate>
                        <div className="grid gap-5 sm:grid-cols-2">
                            <ProfileField label="Mobile / Username" icon={Smartphone}>
                                <input value={profile.mobile} disabled className="profile-input cursor-not-allowed opacity-65" />
                                <p className="mt-1.5 text-[11px] text-muted-foreground">Mobile number cannot be changed here.</p>
                            </ProfileField>

                            <ProfileField label="Full Name" icon={User} error={errors.name} hint={`${profile.name.length}/25`}>
                                <input name="name" value={profile.name} onChange={handleChange} disabled={saving} maxLength={25} autoComplete="name" placeholder="Enter full name" className="profile-input" />
                            </ProfileField>

                            <ProfileField label="Email Address" icon={Mail} error={errors.email}>
                                <input name="email" type="email" value={profile.email} onChange={handleChange} disabled={saving} autoComplete="email" placeholder="name@example.com" className="profile-input" />
                            </ProfileField>

                            <ProfileField label="CSP ID" icon={BadgeCheck} error={errors.cspid} hint={`${profile.cspid.length}/8`}>
                                <input name="cspid" value={profile.cspid} onChange={handleChange} disabled={saving} maxLength={8} placeholder="8-character CSP ID" className="profile-input font-mono tracking-wider uppercase" />
                            </ProfileField>

                            <div className="sm:col-span-2">
                                <ProfileField label="Account Number" icon={Landmark} error={errors.acno} hint={`${profile.acno.length}/20`}>
                                    <input name="acno" type="text" inputMode="numeric" value={profile.acno} onChange={handleChange} disabled={saving} minLength={11} maxLength={20} autoComplete="off" placeholder="Enter 11 to 20 digit account number" className="profile-input font-mono tracking-wider" />
                                </ProfileField>
                            </div>
                        </div>

                        {success && <div className="mt-5 flex items-start gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2.5 text-xs text-emerald-600" role="status"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{success}</div>}

                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button type="button" disabled={saving || !hasChanges} onClick={() => { setProfile(savedProfile); setErrors({}); setSuccess(""); }} className="h-11 rounded-full border border-border px-5 text-sm font-semibold text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground disabled:opacity-50">Discard Changes</button>
                            <button type="submit" disabled={saving || !hasChanges} className="h-11 rounded-full btn-gradient ripple px-6 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                                {saving ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</> : <><Save className="h-4 w-4" />Save Profile</>}
                            </button>
                        </div>
                    </form>
                </section>

                <p className="mt-4 text-center text-[11px] uppercase tracking-[0.26em] text-muted-foreground">Secured by Smart Solutions</p>
            </div>

            <style>{`
                .profile-input { width:100%; height:3rem; padding-left:2.75rem; padding-right:1rem; border-radius:.75rem; background:hsl(var(--background) / .6); border:1px solid hsl(var(--border)); outline:none; font-size:.875rem; transition:border-color 160ms ease,box-shadow 160ms ease,opacity 160ms ease; }
                .profile-input:focus { border-color:hsl(var(--primary)); box-shadow:0 0 0 2px hsl(var(--primary) / .25); }
                .profile-input:disabled { opacity:.65; }
                .profile-input::placeholder { color:hsl(var(--muted-foreground) / .7); }
            `}</style>
        </main>
    );
}

function ProfileField({ label, icon: Icon, error, hint, children }) {
    return (
        <div>
            <div className="flex items-center justify-between gap-3"><label className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</label>{hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}</div>
            <div className="relative mt-2"><Icon className={`pointer-events-none absolute left-4 top-6 h-4 w-4 -translate-y-1/2 ${error ? "text-red-500" : "text-muted-foreground"}`} />{children}</div>
            {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
        </div>
    );
}
